import { mainColors } from '@avantstay/backoffice-core'

import {
  CreateQuoteFormProps,
  HomesForQuotes,
  HomeSleepingPlaces,
} from '@new/domains/bookingsTapeChart/components/CreateQuoteForm/CreateQuoteForm.types'

import {
  QuotesHomeListProps,
  QuotesPreviewProps,
  QuotesTravelAgentProps,
} from '@arriere/types/Quotes.types'

const getAggregatedSleepingArrangements = (sleepingArrangements?: HomeSleepingPlaces) =>
  sleepingArrangements?.reduce(
    (acc, cur) => {
      const existingIndex = acc.findIndex(x => x.name === cur.name)
      return existingIndex >= 0
        ? [
            ...acc.slice(0, existingIndex),
            {
              ...acc[existingIndex],
              count: acc[existingIndex].count + cur.quantity,
            },
            ...acc.slice(existingIndex + 1),
          ]
        : [...acc, { ...cur, count: cur.quantity }]
    },
    [] as { name: string; amenityId: string; count: number }[],
  )

export const parseHome = (home: HomesForQuotes) => {
  const commonHomeFields = {
    id: home.id,
    title: home.title,
    maxOccupancy: home.maxOccupancy,
    roomsCount: home.bedroomsCount,
    bathroomsCount: home.bathroomsCount,
    halfBathroomsCount: home.halfBathroomsCount,
    bedsCount: home.bedsCount,
  }

  const commonAddress = {
    city: home?.address?.city,
  }

  return {
    ...commonHomeFields,
    address: commonAddress,
  }
}

const getGuestName = (name: string) => name.trim().split(' ')[0]

const centsToDollar = (amount: number) => amount / 100

export const parseQuotesToPreviewEmail = (
  createQuoteFormProps: CreateQuoteFormProps,
  taxesIncluded?: boolean,
): QuotesPreviewProps => {
  const { checkIn, checkOut, guests, homes } = createQuoteFormProps

  const coupons =
    homes?.map(home => {
      const homeTotal = home.guestView?.totalOfStay.total ?? 0
      const homeTotalBeforeTaxes = home?.guestView?.preBookingTotalBeforeTaxes.total ?? 0

      const depositAmount = centsToDollar(home.deposits?.total ?? 0)
      const quotePrice = centsToDollar(home.quotePrice ?? 0)
      const quotePriceBeforeTaxes = home?.quotePriceBeforeTaxes ?? 0

      const finalPlainPrice = taxesIncluded ? homeTotal : homeTotalBeforeTaxes
      const finalQuotePrice = taxesIncluded ? quotePrice : quotePriceBeforeTaxes

      return {
        taxesIncluded,
        home: {
          ...parseHome(home),
        },
        regionName: home?.region?.name,
        coverPhoto: home.thumbnail?.url,
        url: '',
        quoteCoupon: {
          plainPrice: finalPlainPrice,
          quotePrice: finalQuotePrice,
          depositPrice: depositAmount,
        },
        bedroomCount: home.bedroomsCount,
      }
    }) ?? []

  return {
    guest: {
      firstName: getGuestName(createQuoteFormProps.contact?.fullName || ''),
    },
    numberOfGuests: guests,
    quote: { checkIn, checkOut },
    coupons: coupons ?? undefined,
    emailBackground: mainColors.neutralExtraLight,
  }
}

export const parseQuotesToPreviewNoPriceEmail = (
  createQuoteFormProps: CreateQuoteFormProps,
): QuotesTravelAgentProps | undefined => {
  const { homes: homesForQuotes } = createQuoteFormProps

  return homesForQuotes
    ? {
        homes: homesForQuotes?.map(home => ({
          home: {
            ...parseHome(home),
            area: home.area,
            state: { code: home?.state?.code },
            images: home?.images?.map(image => ({
              description: image.description ?? '',
              listOrder: image.listOrder,
              url: image.asset.url,
            })),
            url: '',
          },
          images: home?.images?.map(image => ({
            description: image.description ?? '',
            listOrder: image.listOrder,
            url: image.asset.url,
          })),
          region: {
            name: home?.region?.name,
          },
          state: { code: home?.state?.code },
          sleepingArrangements: getAggregatedSleepingArrangements(
            home?.rooms?.flatMap(room =>
              room.sleepingPlaces.map(place => ({
                name: place.name,
                amenityId: place.amenityId,
                quantity: place.quantity,
              })),
            ),
          ),
          longDescription: home.longDescription,
          shortDescription: home.shortDescription,
        })),
        emailBackground: mainColors.neutralExtraLight,
      }
    : undefined
}

export const parseQuotesToPreviewHomesListEmail = (
  createQuoteFormProps: CreateQuoteFormProps,
): QuotesHomeListProps | undefined => {
  const { homes: homesForQuotes, contact } = createQuoteFormProps

  return homesForQuotes
    ? {
        guestName: contact?.fullName || '',
        homes: homesForQuotes?.map(home => ({
          bedroomsCount: home.bedroomsCount,
          sleepingArrangements: getAggregatedSleepingArrangements(
            home?.rooms?.flatMap(room =>
              room.sleepingPlaces.map(place => ({
                name: place.name,
                amenityId: place.amenityId,
                quantity: place.quantity,
              })),
            ),
          ),
          region: { name: home?.region?.name },
          state: { code: home?.state?.code },
          images: home?.images?.map(image => ({
            description: image.description ?? '',
            listOrder: image.listOrder,
            url: image.asset.url,
          })),
          home: {
            ...parseHome(home),
            name: home.title,
            region: { name: home?.region?.name },
            state: { code: home?.state?.code },
            area: home.area,
            images: home?.images?.map(image => ({
              description: image.description ?? '',
              listOrder: image.listOrder,
              url: image.asset.url,
            })),
          },
        })),
        emailBackground: mainColors.neutralExtraLight,
      }
    : undefined
}
